import { type UseMutateAsyncFunction } from '@tanstack/react-query';
import { t } from 'i18next';
import { createSelector } from '@reduxjs/toolkit';

import store, { type RootState } from '../store/store';
import { type MasterDataStore } from '../apis/masterdata/masterdata.type';
import { InterventionStatus } from './interventionButtonMenu.const';
import { InterventionType, type FillInterventionsInformationParams } from '../apis/interventions/intervention.type';
import { type Intervention, type SortElement } from '../components/InterventionsPlanningResult/InterventionPlanningResult.type';
import { initialState, initIntervention } from '../store/interventionSlice';
import { type Customer, PersonTypes } from '../apis/customer/customer.types';
import { getInterventionsByIds, getInterventionsIds, updateIntervention } from '../apis/interventions/interventions';
import { initialBreadcrumb } from '../components/Breadcrumb/Breadcrumb';
import { getFuturePledgeDate } from '../utils/utils';
import { getArticlesDetails } from '../apis/masterdata/masterdata';
import { initialProductCatalog, updateCatalogSearch } from '../store/catalogSlice';
import { selectAllStores } from '../store/store-list';
import { getCurrentDate, getDifferenceInMilliseconds } from '../utils/date/date-utils';
import { type UsePatchInterventionMutationParams } from '../apis/interventions/mutations.types';
import { selectStoreId } from '../store/selectors/userInfo';

interface CheckInterventionResult {
  message: string | false;
  missingField?: Record<string, boolean>;
}

const {
  forReceptionInStore,
  forReceptionInRW,
  processedToBeRedirected,
  sendToRW,
  receivedForProcessingInRW,
  deleted,
  paidClosed,
  closureUnderway,
  processedAndAvailable,
  refusedAndAvailable,
  closed,
  estimate,
  refusedToBeRedirected,
  ogeaValidity,
  RWShortage,
  notStarted,
} = InterventionStatus;

export const finishedInterventions = [
  [InterventionStatus.processedAndAvailable],
  [InterventionStatus.paidClosed],
  [InterventionStatus.deleted],
  [InterventionStatus.closureUnderway],
  [InterventionStatus.closed],
  [InterventionStatus.refusedAndAvailable],
];

export const localPendingInterventions = [
  [InterventionStatus.toProcess, InterventionStatus.receivedForProcessingInRW],
  [InterventionStatus.storeShortage, InterventionStatus.RWShortage],
  [InterventionStatus.ogeaValidity],
  [InterventionStatus.estimate],
];

export const localPendingInterventionsRW = [...localPendingInterventions, [InterventionStatus.forReceptionInRW]];

export const distantPendingInterventions = [
  [InterventionStatus.forReceptionInRW],
  [InterventionStatus.receivedForProcessingInRW],
  [InterventionStatus.RWShortage],
  [InterventionStatus.processedToBeRedirected],
  [InterventionStatus.refusedToBeRedirected],
];

const isLocationDisabled = (stateId: number) => new Set([deleted, paidClosed, closureUnderway, closed]).has(stateId);

export const isInterventionDisabled = (stateId: number) =>
  new Set([deleted, paidClosed, closureUnderway, processedAndAvailable, refusedAndAvailable, closed]).has(stateId);

export const isStateDisabled = (stateId: number, isCreationSite: boolean, interventionType: InterventionType) => {
  if (
    interventionType === InterventionType.web ||
    isInterventionDisabled(stateId) ||
    new Set([forReceptionInRW, forReceptionInStore, processedToBeRedirected, refusedToBeRedirected]).has(stateId)
  ) {
    return true;
  }

  if (isCreationSite) {
    return new Set([receivedForProcessingInRW, RWShortage]).has(stateId);
  }

  return new Set([estimate, sendToRW]).has(stateId);
};

const isInTransitState = (stateId: number) =>
  new Set([forReceptionInRW, forReceptionInStore, processedToBeRedirected, refusedToBeRedirected]).has(stateId);

export const isShortageStateForbidden = (stateId: number) => isInTransitState(stateId) || new Set([ogeaValidity, notStarted]).has(stateId);

/**
 *
 * @deprecated do not use this undocumented method
 */
export const handleInterventionFieldUpdate = async <T, Y extends string | number | boolean>(
  model: T,
  value: Y,
  fieldName: keyof UsePatchInterventionMutationParams['payload'],
  toggleField: (toggle: boolean) => void,
  updateFunction: (model: T) => Record<string, unknown> | Promise<Record<string, unknown>> | void,
  patchIntervention: UseMutateAsyncFunction<unknown, Error, UsePatchInterventionMutationParams, unknown>,
  addErrorToast: (message: string) => void
) => {
  const docNumber = store.getState().intervention.footer.docNumber;

  try {
    if (docNumber) {
      toggleField(true);
      await updateIntervention(docNumber, value, fieldName, patchIntervention);
      await updateFunction(model);
      toggleField(false);
    } else {
      await updateFunction(model);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : t('error.generic.message');
    addErrorToast(message);
  }
};

export const checkGlobalConditions = (
  hasServicesOrSpareParts: boolean,
  isCustomAndWarranty: boolean,
  notInternalAndNoPurchaseDateAndWarranty: boolean
): CheckInterventionResult => {
  if (!hasServicesOrSpareParts) {
    return { message: t('toaster.warning.empty.intervention') };
  }

  if (isCustomAndWarranty) {
    return { message: t('error.message.warranty.with.custom.product') };
  }

  if (notInternalAndNoPurchaseDateAndWarranty) {
    return { message: false, missingField: { purchaseDate: true } };
  }

  return { message: false };
};

export const getMissingFields = (customer: Customer, requiredFields: Set<string>) => {
  const isFrenchCustomer = customer.countryCode === 'FR';
  const missingFields = [];
  requiredFields.has('email') && !customer.email && missingFields.push(t('intervention.page.block.customer.email'));
  if (customer.personType === PersonTypes.customer) {
    requiredFields.has('firstname') && !customer.firstname && missingFields.push(t('searchbox.customer.label.firstname'));
    requiredFields.has('lastname') && !customer.lastname && missingFields.push(t('searchbox.customer.label.lastname'));
  } else {
    requiredFields.has('companyName') && !customer.companyName && missingFields.push(t('searchbox.customer.label.company'));
  }
  requiredFields.has('phone') && !customer.phone && missingFields.push(t('searchbox.customer.label.phone'));
  if (isFrenchCustomer) {
    requiredFields.has('postalCode') && !customer.postalCode && missingFields.push(t('searchbox.customer.label.postalCode'));
    requiredFields.has('city') && !customer.city && missingFields.push(t('searchbox.customer.label.city'));
  }
  return missingFields.join(', ');
};

export const fillInterventionsInformation = ({ interventions, processes = [] }: FillInterventionsInformationParams): Promise<Intervention[]> => {
  const itemCodes = [...new Set(interventions.map(intervention => intervention.depositedProduct.itemCode))].filter(Boolean);

  const productsPromise = Promise.all(
    itemCodes.map(itemCode =>
      itemCode
        ? getArticlesDetails([itemCode])
            .then(([model]) => ({ itemCode, productName: model.modelLib }))
            .catch(() => null)
        : Promise.resolve(null)
    )
  );

  return productsPromise.then(products =>
    interventions.map(intervention => {
      const product = intervention.depositedProduct.itemCode
        ? products.filter(data => !!data).find(data => data?.itemCode === intervention.depositedProduct.itemCode)
        : { productName: intervention.depositedProduct.label };
      // todo remove static redux
      const allStore = selectAllStores(store.getState());
      const creationSite = allStore.find(storeSite => storeSite.id === intervention.creationSite);
      const realisationSite = allStore.find(storeSite => storeSite.id === intervention.realisationSite);

      const constructedIntervention: Intervention = {
        interventionNumber: intervention.documentNumber,
        contextId: intervention.contextId,
        invoiceNumber: intervention.invoiceNumber,
        processLabel: processes.find(process => process.id === intervention.familyId)?.label ?? null,
        processId: intervention.familyId,
        customer: intervention.memberId ?? '',
        phoneNumber: intervention.memberId,
        product: (product ? product.productName : intervention.depositedProduct.itemCode?.toString()) ?? '',
        productCode: intervention.depositedProduct.itemCode,
        customerPledge: intervention.availabilityDate ?? null,
        creationDate: intervention.creationDate,
        realisationSiteId: realisationSite?.id,
        realisationSiteName: realisationSite?.name ?? '',
        creationSiteId: creationSite?.id,
        creationSiteName: creationSite?.name ?? '',
        estimatedTime: intervention.totalTheoreticalTime,
        interventionState: intervention.stateLabel ?? '',
        interventionStateId: intervention.stateId,
        location: intervention.depositedProduct.location,
        interventionType: intervention.interventionType,
        externalReferenceNumber: intervention.externalReferenceNumber,
        services: intervention.services,
        spareParts: intervention.spareParts,
        productPurchaseDate: intervention.depositedProduct.purchaseDate,
        totalPrice: intervention.totalPriceWithTaxes,
        currency: intervention.currencyCode,
        events: intervention.events,
        validateTotalPrice: intervention.validateTotalPrice,
      };
      return constructedIntervention;
    })
  );
};

export const buildInterventionsByIds = (interventionIds: string[], storeId: number, withoutIdentity = false): Promise<Intervention[]> =>
  getInterventionsByIds(interventionIds, ['state'], storeId)
    .then(interventions => interventions.filter(intervention => intervention !== null))
    .then(interventions => fillInterventionsInformation({ interventions, withoutIdentity }));

export const loadNextInterventions = (
  interventionIdsToGet: string[][],
  setInterventionIdsToGet: (ids: string[][]) => void,
  setIsLoading: (status: boolean) => void,
  initiatePlanning: (planning: Intervention[]) => void,
  storeId: number,
  addErrorToast: (error: string) => void
) => {
  if (interventionIdsToGet.length) {
    setIsLoading(true);
    buildInterventionsByIds(interventionIdsToGet[0], storeId)
      .then(nextInterventions => initiatePlanning(nextInterventions))
      .catch(error => addErrorToast(error))
      .finally(() => setIsLoading(false));
    setInterventionIdsToGet([...interventionIdsToGet].splice(1, interventionIdsToGet.length));
  }
};

export const getInterventionIds = (processes: number[], fromDate: Date, toDate: Date, storeId: number): Promise<string[]> =>
  getInterventionsIds({ processes, fromDate, toDate }, storeId).then(toDoEvents =>
    [...toDoEvents]
      .sort((toDoEventA, toDoEventB) => getDifferenceInMilliseconds({ firstDate: toDoEventA.date, secondDate: toDoEventB.date }))
      .flatMap(toDoEvent => toDoEvent.interventionIds)
  );

export const getSortedInterventions = (sortedPlanning: Intervention[], sortElement: SortElement) => {
  const { element, status } = sortElement;
  switch (element) {
    case 'product':
    case 'location':
    case 'interventionState':
    case 'creationSiteName':
    case 'interventionNumber':
      return [...sortedPlanning].sort((a, b) =>
        status ? (a[element] || '').localeCompare(b[element] || '') : (b[element] || '').localeCompare(a[element] || '')
      );
    case 'creationDate':
    case 'customerPledge':
      return [...sortedPlanning].sort((a, b) => {
        const firstDate = a[element];
        const secondDate = b[element];

        if (!firstDate && !secondDate) {
          return 0;
        }

        if (!firstDate) {
          return 1;
        }

        if (!secondDate) {
          return -1;
        }

        if (status) {
          return getDifferenceInMilliseconds({ firstDate, secondDate });
        }

        return getDifferenceInMilliseconds({ firstDate: secondDate, secondDate: firstDate });
      });
    case 'estimatedTime':
      return [...sortedPlanning].sort((a, b) => (status ? (a[element] || 0) - (b[element] || 0) : (b[element] || 0) - (a[element] || 0)));
    default:
      return sortedPlanning;
  }
};

export const duplicateIntervention = (addSuccessToast: (message: string) => void) => {
  const state = store.getState();
  const selectedSite = state.userInfo.selectedSite;
  const creationSite: MasterDataStore = {
    id: selectedSite.id,
    name: selectedSite.shortName,
  };
  const isPaidClosed = state.intervention.footer.state.stateId === InterventionStatus.paidClosed;

  store.dispatch(
    initIntervention({
      ...initialState,
      businessType: state.intervention.businessType,
      interventionType: InterventionType.standard,
      creationSite,
      realizationSite: {
        ...creationSite,
        delay: state.intervention.realizationSite?.delay,
      },
      footer: {
        ...initialState.footer,
        pledgeDate: isPaidClosed ? getFuturePledgeDate(getCurrentDate()) : state.intervention.footer.pledgeDate,
      },
    })
  );
  store.dispatch(
    updateCatalogSearch({
      product: { ...initialProductCatalog },
      breadcrumb: [initialBreadcrumb()],
      fetchNeeded: true,
      defaultImageUrl: undefined,
    })
  );
  addSuccessToast(t('toaster.intervention.duplication.success'));
};

export const isTraceabilityNumberInvalid = (traceabilityNumber: string) => traceabilityNumber.length < 9;

export const formatStoreIdForWBO = (storeIdToFormat: number | undefined) => {
  const paddedStoreId = storeIdToFormat?.toString().padStart(5, '0');
  return `007${paddedStoreId}${paddedStoreId}`;
};

export const selectIsLocationDisabled = (state: RootState) => isLocationDisabled(state.intervention.footer.state.stateId);

export const selectIsFullyDisabled = (state: RootState) => isInterventionDisabled(state.intervention.footer.state.stateId);

export const selectIsSpecificationsDisabled = (state: RootState) =>
  isInterventionDisabled(state.intervention.footer.state.stateId) || isInTransitState(state.intervention.footer.state.stateId);

export const selectIsShortageStateForbidden = (state: RootState) => isShortageStateForbidden(state.intervention.footer.state.stateId);

export const selectIsStateDisabled = createSelector(
  (state: RootState) => state.intervention.footer.state.stateId,
  (state: RootState) => state.intervention.creationSite?.id === selectStoreId(state),
  (state: RootState) => state.intervention.interventionType,
  (stateId, isCreationSite, interventionType) => isStateDisabled(stateId, isCreationSite, interventionType)
);
